import { LETTER_COUNT, letterChar, letterIndexOf, lookalikeLetters } from "./letters.js";
import { layOutOptions, mulberry32, seedFrom, shuffle } from "./random.js";
/**
 * Which word is this letter hiding in?
 *
 * A letter at the top and a few words underneath; the child taps the word that contains it. The
 * companion to the alphabet game and the step after it: that one teaches what a letter looks like,
 * including the shapes it wears inside a word, and this one asks the child to go and find one.
 *
 * **A pre-reader can play this, and that is the point.** Nothing here asks what a word says. The
 * child is matching a shape against four small pictures made of shapes, which is exactly what
 * reading is before it is reading — and a child who can do it has the skill that makes the rest
 * follow. A game that needed the words read would be a game only children who can already read
 * could play, which is the wrong way round.
 *
 * **Why words on cards rather than a letter to tap inside a sentence.** The first design put a
 * sentence on screen and asked the child to tap the letter in it. It is unbuildable on a phone: a
 * finger misses a target under roughly 76 px, a 360 px screen fits about four such targets in a
 * row, and a sentence has a dozen letters. Anything that fitted would have been scoring motor
 * accuracy. A word on a card is one large target, the whole card, and the question is the same one.
 */
// ---------------------------------------------------------------------------------------
// The words
// ---------------------------------------------------------------------------------------
/**
 * A closed vocabulary, versioned with the bundle like every other stimulus.
 *
 * Chosen against four rules, all of which a test enforces rather than trusting:
 *
 *   1. **Every one of the thirty-two letters appears in at least one word exactly once.** A letter
 *      with no word cannot be taught here, and the rare ones are the ones that get forgotten —
 *      hence ژله, ظرف, کثیف and حوض, which are here to carry ژ, ظ, ث and ض and for no other reason.
 *   2. **A five-year-old knows the thing.** Concrete nouns a preschool classroom already says out
 *      loud. Nothing abstract, because a child who cannot picture the word has been given a puzzle
 *      about vocabulary instead of about letters.
 *   3. **Persian ک and ی**, never the Arabic ك and ي, which look the same in most fonts and are
 *      different characters — a word typed with the wrong one would silently never match its letter.
 *   4. **Nothing outside the thirty-two.** No آ, no hamza, no half-space, no diacritics. آ is the
 *      real cost of this rule and it is paid deliberately: آ and ا are a different نشانه with the
 *      same body, so a word like آش among the options for ا would be a wrong answer for a defensible
 *      reason, which is the worst kind. It shuts out آب and آش, which is a genuine loss.
 */
const WORDS = [
    // Two and three letters — the short words the first levels are built from.
    "پا", "در", "گل", "سگ", "قو", "یخ", "خط", "ماه", "نان", "باد", "ابر", "اسب",
    "توپ", "چتر", "سیب", "شیر", "فیل", "میز", "رود", "دست", "برف", "برگ", "بره",
    "پسر", "پل", "چوب", "خرس", "ذرت", "قند", "گاو", "هوا", "صبح", "حوض", "شمع",
    "عسل", "مرغ", "باغ", "تاب", "کفش", "فرش", "چشم", "هفت", "هلو", "ژله", "قاشق",
    // Four and five — the middle of the ladder.
    "کتاب", "مداد", "مادر", "پدر", "خانه", "جاده", "جوجه", "شانه", "لباس", "زمین",
    "کلید", "دفتر", "درخت", "گردو", "پنیر", "ژاکت", "کثیف", "مثلث", "ظرف", "طناب",
    "جنگل", "زنبور", "مریض", "کاغذ", "غذا", "بابا", "انار", "اتاق", "ترازو", "ورزش",
    "خورشید", "ستاره", "پرنده", "روباه", "صابون", "لیوان", "نردبان", "باران", "طوطی",
    // Short words carrying the rare letters, so those are teachable low down at least.
    "ظهر", "دریا", "کوچه", "هویج", "کاهو", "چراغ", "کلاغ", "پلنگ", "تلفن", "ستون", "قایق",
    /**
     * A SECOND short word for the letters that had only one.
     *
     * A letter gets two trials in a row so it is practised rather than glimpsed, and with one word in
     * range the second trial could only show the word the child had just answered. Nine letters were
     * in that position at the first level — ز had only میز, ک only کفش, ط only خط — so a quarter of
     * the game's opening repeated itself. These exist to be the alternative and for nothing else.
     *
     * **The last few stretch the "a five-year-old knows the thing" rule and it is worth admitting.**
     * ثمر, ضد and ژن are words a child meets later than the rest of this list. Persian offers almost
     * nothing two or three letters long carrying ث, ض or ژ once, and the choice was between a slightly
     * unfamiliar word and showing the same word twice. A child is being asked to FIND a shape here,
     * not to define what they found, so the unfamiliar word costs them nothing.
     */
    "سبز", "زنگ", "کوه", "کار", "نخ", "طلا", "صد", "عصا", "تاج", "جو", "ثمر", "ضد", "ژن",
    /**
     * The long words, and there are a lot of them for one reason: the top of the ladder asks for
     * five to seven letters, and with only a handful of those the level ran out of wrong answers and
     * threw. A ladder whose last rung has no content is not a ladder.
     *
     * Five letters is where a preschool vocabulary starts thinning out, which is why these are
     * animals, food and things in a street — the categories where a six-year-old still knows the
     * long words.
     */
    "دیوار", "بادام", "گنجشک", "خرگوش", "پنجره", "سنجاب", "شلوار", "انگور", "کبوتر", "ماشین",
    "صندلی", "خربزه", "زرافه", "عروسک", "مسواک", "بستنی", "شکلات", "مغازه", "بشقاب", "طاووس",
    "گوسفند", "پروانه", "دوچرخه", "پرتقال", "خیابان", "بادکنک", "پرستار", "هندوانه",
    /**
     * Words holding a letter in the MIDDLE, for the band that asks for exactly that.
     *
     * The upper levels want the letter joined on both sides, and six letters had only one word each
     * that could do it — so the second trial of those letters had to repeat the first. A letter at the
     * start or the end of a word is easy to come by and a letter buried inside one is not, which is
     * also why that band is the hard one.
     */
    "بچه", "تخته", "یخچال", "قصه", "نقاش", "دامن", "بهار", "باغبان", "شکوفه", "پارچه",
];
/** How many words the bundle has. Exported so a caller can bound an index it read back. */
export const WORD_COUNT = WORDS.length;
/**
 * The word at an index.
 *
 * **The index of a word in the list is recorded in every trial**, because `trial_start` stores a
 * stimulus as integers. Reordering the list therefore silently rewrites history. Words may be
 * appended; nothing may be moved or removed without a new content bundle.
 */
export function wordText(wordIndex) {
    const word = WORDS[wordIndex];
    if (word === undefined)
        throw new Error(`no word at index ${wordIndex}`);
    return word;
}
/** The letters of a word, as alphabet indices, in reading order. */
const lettersOf = (word) => [...word].map(letterIndexOf);
/** Precomputed once: every word's letters, so a trial is assembled with lookups rather than scans. */
const LETTERS_IN = WORDS.map(lettersOf);
/** How many times a letter appears in a word. */
const countIn = (wordIndex, letterIndex) => (LETTERS_IN[wordIndex] ?? []).filter((l) => l === letterIndex).length;
/**
 * Whether the letter sits INSIDE the word rather than at either end.
 *
 * The hard case, and the top of the ladder. A letter at the start or the end of a word keeps a
 * shape close to the one the child learnt it by; in the middle it is joined on both sides and looks
 * least like itself. Finding it there is the skill that makes reading possible.
 */
const sitsInside = (wordIndex, letterIndex) => {
    const letters = LETTERS_IN[wordIndex] ?? [];
    return letters.some((l, i) => l === letterIndex && i > 0 && i < letters.length - 1);
};
/**
 * The ten levels.
 *
 * Written out one by one rather than computed, for the same reason every other ladder here is: the
 * sequence is the curriculum and a formula would hide it.
 *
 *   1–3   *the letter is in one of these words.* Short words, and the wrong ones share nothing with
 *         the letter being looked for. A child who knows the shape can do this at a glance.
 *   4–6   *the wrong words start looking right.* One, then two, then all three carry a letter on the
 *         target's own skeleton, so a glance is no longer enough and the dots have to be read.
 *   7–9   *the letter is hiding in the middle.* Where it is joined on both sides and looks least
 *         like the letter the child learnt, which is the case that matters for reading.
 *   10    both, with no limit on how long the words are.
 */
function recipeFor(difficulty) {
    switch (difficulty) {
        // Three cards, and only here — the first trial of a first sitting should be winnable by looking.
        case 1:
            return { optionCount: 3, minLength: 2, maxLength: 3, lookalikes: 0, inside: false };
        case 2:
            return { optionCount: 4, minLength: 2, maxLength: 3, lookalikes: 0, inside: false };
        case 3:
            return { optionCount: 4, minLength: 2, maxLength: 4, lookalikes: 0, inside: false };
        case 4:
            return { optionCount: 4, minLength: 3, maxLength: 4, lookalikes: 1, inside: false };
        case 5:
            return { optionCount: 4, minLength: 3, maxLength: 5, lookalikes: 2, inside: false };
        case 6:
            return { optionCount: 4, minLength: 3, maxLength: 5, lookalikes: 3, inside: false };
        case 7:
            return { optionCount: 4, minLength: 3, maxLength: 5, lookalikes: 1, inside: true };
        case 8:
            return { optionCount: 4, minLength: 4, maxLength: 6, lookalikes: 2, inside: true };
        case 9:
            return { optionCount: 4, minLength: 4, maxLength: 7, lookalikes: 3, inside: true };
        case 10:
            return { optionCount: 4, minLength: 5, maxLength: 7, lookalikes: 3, inside: true };
        default:
            throw new Error(`no word level ${difficulty}`);
    }
}
/**
 * How many trials in a row are about one letter before the alphabet moves on.
 *
 * Two, matching the alphabet game, and for the same reason: a twelve-trial sitting becomes six
 * letters, which is a lesson a teacher could name.
 */
const TRIALS_PER_LETTER = 2;
export function generateWordTrial(task, formId, trialIndex, difficulty) {
    const { minDifficulty, maxDifficulty } = task.measurement;
    if (difficulty < minDifficulty || difficulty > maxDifficulty) {
        throw new Error(`difficulty ${difficulty} outside ${minDifficulty}..${maxDifficulty} for ${task.id}`);
    }
    const recipe = recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    const fits = (wordIndex) => {
        const length = (WORDS[wordIndex] ?? "").length;
        return length >= recipe.minLength && length <= recipe.maxLength;
    };
    /** Words that could be the answer for a letter, at this level. */
    const answersFor = (letterIndex) => WORDS.map((_, i) => i).filter((i) => fits(i) &&
        // Exactly once, so "which word has it" has one answer and no trial turns on counting.
        countIn(i, letterIndex) === 1 &&
        (!recipe.inside || sitsInside(i, letterIndex)));
    /**
     * The letter, walked through the alphabet rather than drawn from it.
     *
     * The same rule as the alphabet game, and for the same reason: a class is taught ا, then ب, then
     * پ, and a teacher who spent the week on the first four does not want the fifth arriving by
     * chance. The cursor walks all thirty-two and the LEVEL decides where it can stop — a level
     * demanding a five-letter word with the letter buried in the middle simply has fewer letters it
     * can ask about, and the cursor lands on the nearest one it can serve.
     */
    // Always from the beginning. See `letters.ts` — the letter order is the curriculum and is fixed;
    // the variety lives in the cards, which the form reseeds every sitting.
    const block = Math.floor(trialIndex / TRIALS_PER_LETTER);
    const cursor = block;
    const teachable = Array.from({ length: LETTER_COUNT }, (_, i) => i).filter((i) => answersFor(i).length > 0);
    /**
     * Counted through the letters this level can serve, not through the alphabet index.
     *
     * Some letters have no word of the required length carrying them once — ژ in a five-letter word,
     * ث in a three-letter one — so a level genuinely cannot ask about them. Taking "the next letter at
     * or after the cursor" made the skipped positions pile onto their neighbour, and a sitting opened
     * with four trials of the same letter. Stepping through the serviceable list keeps two trials each
     * and the order stays alphabetical, because the list is.
     */
    const letter = teachable[cursor % teachable.length];
    if (letter === undefined) {
        throw new Error(`no letter can be taught at level ${difficulty} — no word carries one`);
    }
    /**
     * A different word each time the letter comes round.
     *
     * **The two trials of one letter were landing on the same word a quarter of the time**, which is
     * the most visible kind of repetition there is: the child is asked about ث, finds it in a word,
     * and is immediately asked about ث and shown the same word. It happened because each trial drew
     * its own shuffle, and an independent draw from a short list repeats — for the rare letters the
     * list is two words long, so it repeated constantly.
     *
     * The fix is to shuffle ONCE per letter and then step through the result. The seed is the letter's
     * block rather than the trial, so both trials of a letter see the same ordering and take different
     * positions in it; only when a letter has a single word in range can it possibly repeat, and then
     * there is nothing else to show.
     */
    const candidates = shuffle(answersFor(letter), mulberry32(seedFrom(`${formId}:word`, block, difficulty)));
    const answer = candidates[(trialIndex % TRIALS_PER_LETTER) % candidates.length];
    if (answer === undefined)
        throw new Error(`no word holds ${letterChar(letter)} once`);
    /**
     * The wrong words, which must not contain the target letter at all.
     *
     * Preferring the ones that carry a lookalike — a letter on the same skeleton — up to the number
     * the level asks for, and filling from the rest. Preferred rather than required: the target's
     * family may be small, or short enough words carrying it may not exist, and a level that refused
     * to build rather than settle for an easier distractor would exclude those letters entirely.
     */
    /**
     * A wrong word must not contain the letter at all, and must be about as long as the right one.
     *
     * **Length is matched to the ANSWER rather than to the level, and the difference matters twice.**
     * Drawing wrong words from the level's own band was too narrow — at the top, where the band is
     * five to seven letters, the pool ran dry and the generator threw. Drawing them from the whole
     * list would be worse: the answer would stand out by size, and "tap the longest card" is a rule a
     * child would find in an afternoon. Within one letter of the answer's length gives a wide pool
     * and cards that all look like plausible answers.
     */
    const answerLength = (WORDS[answer] ?? "").length;
    const mates = new Set(lookalikeLetters(letter));
    const clean = WORDS.map((_, i) => i).filter((i) => i !== answer &&
        countIn(i, letter) === 0 &&
        Math.abs((WORDS[i] ?? "").length - answerLength) <= 1);
    const withMate = shuffle(clean.filter((i) => (LETTERS_IN[i] ?? []).some((l) => mates.has(l))), rand);
    const without = shuffle(clean.filter((i) => !(LETTERS_IN[i] ?? []).some((l) => mates.has(l))), rand);
    const wanted = recipe.optionCount - 1;
    const picked = [];
    const take = (from, count) => {
        for (const wordIndex of from) {
            if (picked.length >= wanted || count <= 0)
                return;
            if (picked.includes(wordIndex))
                continue;
            picked.push(wordIndex);
            count--;
        }
    };
    take(withMate, recipe.lookalikes);
    take(without, wanted - picked.length);
    // Whatever is still missing, from wherever it can be had.
    take(withMate, wanted - picked.length);
    if (picked.length < wanted) {
        throw new Error(`only ${picked.length} wrong words for ${letterChar(letter)} at level ${difficulty} — a ` +
            "trial with too few wrong answers must never reach a child");
    }
    // Laid out as a whole, so where a card sits says nothing (`layOutOptions`).
    const { options, correctIndex } = layOutOptions(answer, picked, rand);
    return {
        target: [letter],
        options: options.map((wordIndex) => [wordIndex]),
        correctIndex,
    };
}
/** The chosen card must be the word holding the letter. */
export function scoreWordTrial(correctIndex, chosenIndex) {
    return correctIndex === chosenIndex;
}
//# sourceMappingURL=words.js.map